import React, { useState, useEffect } from 'react';
import { useDomain } from '../contexts/DomainContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import DashboardLayout from '../components/Layout/DashboardLayout';
import {
  GlobeAltIcon,
  ChartBarIcon,
  LinkIcon,
  DocumentTextIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  ClockIcon,
  SparklesIcon,
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentMagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import { Helmet } from 'react-helmet';
import FAQSection from '../components/FAQSection';
import PageAnalysisTab from '../components/PageAnalysisTab';

const DomainAnalysis = () => {
    const { selectedDomain } = useDomain();

  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [lastRefreshed, setLastRefreshed] = useState(null);

  
  // Load saved domain analysis on mount
  
  // Sync with global domain context
  useEffect(() => {
    if (selectedDomain) {
      setDomain(selectedDomain);
    }
  }, [selectedDomain]);

  useEffect(() => {
    const savedDomain = localStorage.getItem('lastAnalysisDomain');
    if (savedDomain) {
      setDomain(savedDomain);
      const token = localStorage.getItem('token');
      if (token) {
        axios.get(`/api/analysis/saved-domain/${savedDomain}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => {
          if (res.data.ok && res.data.report) {
            const report = res.data.report;
            setAnalysis(report.data);
            setLastRefreshed(new Date(report.updated_at).toLocaleDateString());
          }
        }).catch(() => {});
      }
    }
  }, []);

  const handleAnalyze = async () => {
    if (!domain.trim()) {
      toast.error('Please enter a domain');
      return;
    }

    // Clean domain input
    let cleanDomain = domain.trim().toLowerCase();
    cleanDomain = cleanDomain.replace(/^https?:\/\//, '').replace(/\/$/, '');

    setLoading(true);
    try {
      const response = await axios.post('/api/analysis/scan', { url: `https://${cleanDomain}` });
      setAnalysis(response.data);
        localStorage.setItem('lastAnalysisDomain', cleanDomain);
        setLastRefreshed(new Date().toLocaleDateString());
      toast.success('Domain analysis completed!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to analyze domain');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBackground = (score) => {
    if (score >= 80) return 'from-green-500 to-green-600';
    if (score >= 60) return 'from-yellow-500 to-yellow-600';
    return 'from-red-500 to-red-600';
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'pages', name: 'Pages Analysis', icon: DocumentMagnifyingGlassIcon },
    { id: 'technical', name: 'Technical SEO', icon: ShieldCheckIcon },
    { id: 'content', name: 'Content', icon: DocumentTextIcon },
    { id: 'backlinks', name: 'Backlinks', icon: LinkIcon },
    { id: 'ai', name: 'AI Visibility', icon: SparklesIcon }
  ];

  const CircularProgress = ({ score, size = 120 }) => {
    const radius = size / 2 - 10;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (score / 100) * circumference;

    return (
      <div className="relative inline-flex items-center justify-center">
        <svg width={size} height={size} className="transform -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#e5e7eb"
            strokeWidth="8"
            fill="none"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="url(#gradient)"
            strokeWidth="8"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-all duration-1000 ease-out"
          />
          <defs>
            <linearGradient id="gradient">
              <stop offset="0%" stopColor="#3B82F6" />
              <stop offset="100%" stopColor="#8B5CF6" />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute flex flex-col items-center">
          <span className={`text-3xl font-bold ${getScoreColor(score)}`}>{score}</span>
          <span className="text-xs text-gray-600">Score</span>
        </div>
      </div>
    );
  };


  const DOMAIN_FAQ = [
    { q: "What is domain analysis and how does it help with SEO?", a: "Domain analysis is a comprehensive evaluation of a website's SEO health, authority, and performance metrics. It examines factors like domain authority, backlink profile, technical SEO issues, content quality, page speed, and search visibility. SERPfinity's domain analysis provides actionable insights to identify strengths, weaknesses, and opportunities to improve your search rankings and online presence." },
    { q: "What metrics does SERPfinity analyze for domain assessment?", a: "SERPfinity evaluates over 50 domain metrics including Domain Authority score, organic traffic estimates, backlink quantity and quality, referring domains, top-ranking keywords, technical SEO health score, Core Web Vitals, mobile usability, structured data implementation, AI search visibility score, content quality metrics, and competitive positioning across both traditional and AI search platforms." },
    { q: "How does domain authority affect search rankings?", a: "Domain authority is a composite score predicting how well a website will rank in search results. It is influenced by the quality and quantity of backlinks, content relevance, site age, technical health, and user engagement signals. While not a direct Google ranking factor, domain authority correlates strongly with ranking performance. Higher authority domains typically earn more organic traffic and AI search citations." },
    { q: "Can I analyze competitor domains with SERPfinity?", a: "Yes, SERPfinity enables comprehensive competitor domain analysis. Compare your domain metrics side-by-side with competitors to identify content gaps, backlink opportunities, keyword overlap, and areas where competitors outperform you. Our competitive intelligence features reveal competitor strategies in both traditional search and AI platforms, helping you develop data-driven strategies to gain market share." },
    { q: "What is the difference between domain analysis and site audit?", a: "Domain analysis provides a high-level overview of a website's overall SEO health, authority, and competitive positioning. A site audit dives deeper into technical issues, crawling specific pages to identify broken links, missing meta tags, duplicate content, schema errors, and other on-page issues. SERPfinity offers both tools to give you complete visibility from strategic overview to granular technical details." },
    { q: "How can I improve my domain authority score?", a: "Improving domain authority requires a multi-faceted approach. Focus on earning high-quality backlinks from authoritative sites through content marketing and digital PR. Create comprehensive, expert-level content that attracts natural links. Fix technical SEO issues that hinder crawling and indexing. Improve user experience metrics like page speed and mobile usability. Build brand mentions and entity signals across the web for AI search visibility." }
  ];

  return (
    <>
      <Helmet>
        <title>Domain Analysis Tool | SEO & AEO Domain Assessment | SERPfinity</title>
        <meta name="description" content="Comprehensive domain analysis for SEO and AEO optimization. Evaluate domain authority, backlinks, technical health, AI visibility, and competitive positioning with SERPfinity." />
        <meta name="keywords" content="domain analysis tool, domain authority checker, SEO domain assessment, backlink analyzer, competitor domain analysis, website health check, AEO domain analysis, SERPfinity" />
        <link rel="canonical" href="https://app.serpfinity.ai/domain-analysis" />
        <meta property="og:title" content="Domain Analysis Tool | SERPfinity" />
        <meta property="og:description" content="Evaluate any domain SEO health, authority, and AI search visibility with comprehensive analysis." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://app.serpfinity.ai/domain-analysis" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Domain Analysis Tool | SERPfinity" />
        <meta name="twitter:description" content="Comprehensive domain analysis for SEO professionals. Check authority, backlinks, and AI visibility." />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify({"@context":"https://schema.org","@type":"FAQPage","mainEntity":DOMAIN_FAQ.map(item=>({"@type":"Question","name":item.q,"acceptedAnswer":{"@type":"Answer","text":item.a}}))})}</script>
      </Helmet>
    <DashboardLayout 
      title="Domain Analysis" 
      subtitle="Comprehensive SEO and AEO analysis for any domain"
    >
      <div className="max-w-7xl mx-auto">

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex gap-4">
            <div className="flex-1">
              <input
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAnalyze()}
                placeholder="Enter domain to analyze (e.g., example.com)"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleAnalyze}
              disabled={loading}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Analyzing...
                </span>
              ) : (
                'Analyze Domain'
              )}
            </button>
          </div>
        </div>

        {/* Results */}
        {analysis && (
          <>
            {/* Overall Score Card */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="flex flex-col items-center">
                  <CircularProgress score={analysis.overallScore || 75} />
                  <h3 className="mt-3 font-semibold text-gray-900">Overall Score</h3>
                  <p className="text-sm text-gray-600">Combined SEO/AEO Score</p>
                </div>

                <div className="col-span-3">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-blue-600">Page Speed</span>
                        <ClockIcon className="h-5 w-5 text-blue-500" />
                      </div>
                      <p className="text-2xl font-bold text-blue-900">
                        {analysis.performance?.speed || 'N/A'}s
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm" style={{color:"#A61F60"}}>Domain Authority</span>
                        <StarIcon className="h-5 w-5 text-green-500" />
                      </div>
                      <p className="text-2xl font-bold text-gray-900">
                        {analysis.authority || 45}/100
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm" style={{color:"#2563EB"}}>AI Visibility</span>
                        <SparklesIcon className="h-5 w-5 text-purple-500" />
                      </div>
                      <p className="text-2xl font-bold text-gray-900">
                        {analysis.aiScore || 82}/100
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm" style={{color:"#A61F60"}}>Backlinks</span>
                        <LinkIcon className="h-5 w-5 text-orange-500" />
                      </div>
                      <p className="text-2xl font-bold text-gray-900">
                        {analysis.backlinks?.total ?? 0}
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-indigo-600">Keywords</span>
                        <ChartBarIcon className="h-5 w-5 text-indigo-500" />
                      </div>
                      <p className="text-2xl font-bold text-indigo-900">
                        {analysis.keywords?.ranking || 342}
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-pink-600">Content Score</span>
                        <DocumentTextIcon className="h-5 w-5 text-pink-500" />
                      </div>
                      <p className="text-2xl font-bold text-pink-900">
                        {typeof analysis.contentScore === 'object' ? analysis.contentScore?.overall || 78 : analysis.contentScore || 78}/100
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="border-b">
                <nav className="flex -mb-px">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-3 px-6 flex items-center space-x-2 border-b-2 font-medium text-sm transition-colors ${
                        activeTab === tab.id
                          ? 'border-purple-500 text-purple-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <tab.icon className="h-5 w-5" />
                      <span>{tab.name}</span>
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">SEO Overview</h3>
                    
                    {/* Issues Summary */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center">
                          <XCircleIcon className="h-8 w-8 text-red-500 mr-3" />
                          <div>
                            <p className="text-2xl font-bold text-gray-900">{analysis.issues?.critical || 0}</p>
                            <p className="text-sm text-red-700">Critical Issues</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-center">
                          <ExclamationTriangleIcon className="h-8 w-8 text-yellow-500 mr-3" />
                          <div>
                            <p className="text-2xl font-bold text-yellow-900">{analysis.issues?.warnings || 0}</p>
                            <p className="text-sm text-yellow-700">Warnings</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center">
                          <CheckCircleIcon className="h-8 w-8 text-green-500 mr-3" />
                          <div>
                            <p className="text-2xl font-bold text-gray-900">{analysis.issues?.passed || 0}</p>
                            <p className="text-sm text-green-700">Passed Checks</p>
                          </div>
                        </div>
                      </div>
                    </div>

              {/* Top Issues */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Top Issues to Fix</h4>
                <div className="space-y-3">
                  {(Array.isArray(analysis.topIssues) ? analysis.topIssues : analysis.issues || []).slice(0, 5).map((issue, index) => (
                    <div key={index} className="flex items-start p-3 bg-gray-50 rounded-lg">
                      <div className={`mt-0.5 mr-3 ${
                        issue.severity === 'high' || issue.severity === 'critical' ? 'text-red-500' :
                        issue.severity === 'medium' || issue.severity === 'warning' ? 'text-yellow-500' :
                        'text-blue-500'
                      }`}>
                        {(issue.severity === 'high' || issue.severity === 'critical') ? <XCircleIcon className="h-5 w-5" /> :
                          (issue.severity === 'medium' || issue.severity === 'warning') ? <ExclamationTriangleIcon className="h-5 w-5" /> :
                            <LightBulbIcon className="h-5 w-5" />}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{issue.message || issue.title || 'Issue detected'}</p>
                        <p className="text-sm text-gray-600 mt-1">{issue.fix || issue.description || 'See recommendations for details'}</p>
                        {issue.url && <p className="text-xs text-blue-400 mt-1">URL: {issue.url}</p>}
                        <p className="text-xs text-blue-600 mt-2 cursor-pointer hover:underline" onClick={() => setSelectedIssue(issue)}>
                          How to fix &rarr;
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        issue.severity === 'high' || issue.severity === 'critical' ? 'bg-red-100 text-red-700' :
                        issue.severity === 'medium' || issue.severity === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {issue.severity || 'medium'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Passed Checks */}
              {analysis.passedChecks && analysis.passedChecks.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-white mb-4">Passed Checks</h3>
                  <div className="space-y-3">
                    {analysis.passedChecks.map((check, index) => (
                      <div key={index} className="bg-gray-800/50 rounded-lg p-4 border border-green-500/20 flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <CheckCircleIcon className="h-5 w-5 text-green-500" />
                          <div>
                            <p className="text-white font-medium">{check.title}</p>
                            {check.url && <p className="text-xs text-blue-400 mt-1">URL: {check.url}</p>}
                          </div>
                        </div>
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">passed</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </div>
        )}



          {/* Pages Analysis Tab */}
          {activeTab === 'pages' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Pages Analysis</h3>
              <div className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left p-3 text-gray-400 text-sm">URL</th>
                      <th className="text-left p-3 text-gray-400 text-sm">Title</th>
                      <th className="text-left p-3 text-gray-400 text-sm">Status</th>
                      <th className="text-left p-3 text-gray-400 text-sm">Word Count</th>
                      <th className="text-left p-3 text-gray-400 text-sm">Issues</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-700/50">
                      <td className="p-3">
                        <a href={analysis.urlScanned} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline text-sm">
                          {analysis.urlScanned || analysis.domain}
                        </a>
                      </td>
                      <td className="p-3 text-sm text-white">{analysis.technicalSeo?.title || 'N/A'}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${analysis.technicalSeo?.hasTitle && analysis.technicalSeo?.hasMetaDesc ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {analysis.technicalSeo?.hasTitle && analysis.technicalSeo?.hasMetaDesc ? 'Healthy' : 'Needs Work'}
                        </span>
                      </td>
                      <td className="p-3 text-sm text-white">{analysis.technicalSeo?.wordCount || 0}</td>
                      <td className="p-3 text-sm text-white">{analysis.issues?.critical + analysis.issues?.warnings || 0}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h4 className="text-md font-semibold mt-6 mb-3">Page Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <h5 className="text-sm text-gray-400 mb-3">Meta Information</h5>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-400">Title Tag</span>
                      <span className="text-sm text-white">{analysis.technicalSeo?.hasTitle ? 'Present' : 'Missing'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-300">Meta Description</span>
                      <span className="text-sm text-white">{analysis.technicalSeo?.hasMetaDesc ? 'Present' : 'Missing'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-300">Canonical Tag</span>
                      <span className="text-sm text-white">{analysis.technicalSeo?.hasCanonical ? 'Present' : 'Missing'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-300">Mobile Optimized</span>
                      <span className="text-sm text-white">{analysis.technicalSeo?.mobileOptimized ? 'Yes' : 'No'}</span>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <h5 className="text-sm text-gray-400 mb-3">Content Structure</h5>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-400">Word Count</span>
                      <span className="text-sm text-white">{analysis.technicalSeo?.wordCount || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-400">H1 Tags</span>
                      <span className="text-sm text-white">{analysis.technicalSeo?.headings?.h1 || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-400">H2 Tags</span>
                      <span className="text-sm text-white">{analysis.technicalSeo?.headings?.h2 || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-400">Images</span>
                      <span className="text-sm text-white">{analysis.technicalSeo?.images?.total || 0} ({analysis.technicalSeo?.images?.missingAlt || 0} missing alt)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-400">Links</span>
                      <span className="text-sm text-white">{analysis.technicalSeo?.links || 0}</span>
                    </div>
                  </div>
                </div>
              </div>

              {analysis.technicalSeo?.title && (
                <div className="mt-4 bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <h5 className="text-sm text-gray-400 mb-2">Page Title</h5>
                  <p className="text-white text-sm">{analysis.technicalSeo.title}</p>
                  <p className="text-xs text-gray-500 mt-1">{analysis.technicalSeo.title.length} characters</p>
                </div>
              )}
              {analysis.technicalSeo?.metaDescription && (
                <div className="mt-4 bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <h5 className="text-sm text-gray-400 mb-2">Meta Description</h5>
                  <p className="text-white text-sm">{analysis.technicalSeo.metaDescription}</p>
                  <p className="text-xs text-gray-500 mt-1">{analysis.technicalSeo.metaDescription.length} characters</p>
                </div>
              )}
            </div>
          )}


                {/* Technical SEO Tab */}
                {activeTab === 'technical' && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Technical SEO Analysis</h3>
                    <div className="grid grid-cols-2 gap-6">
                      <div className="bg-gray-800 rounded-lg p-4">
                        <h4 className="text-md font-semibold mb-3">Security & Accessibility</h4>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-300">SSL Certificate</span>
                            <span className={`text-sm font-medium ${analysis.technicalSeo?.hasSSL ? 'text-green-400' : 'text-red-400'}`}>
                              {analysis.technicalSeo?.hasSSL ? 'Secure' : 'Not Secure'}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-300">Mobile Optimized</span>
                            <span className={`text-sm font-medium ${analysis.technicalSeo?.mobileOptimized ? 'text-green-400' : 'text-red-400'}`}>
                              {analysis.technicalSeo?.mobileOptimized ? 'Yes' : 'No'}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-300">Canonical Tag</span>
                            <span className={`text-sm font-medium ${analysis.technicalSeo?.hasCanonical ? 'text-green-400' : 'text-red-400'}`}>
                              {analysis.technicalSeo?.hasCanonical ? 'Present' : 'Missing'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-800 rounded-lg p-4">
                        <h4 className="text-md font-semibold mb-3">Crawlability</h4>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-300">Robots.txt</span>
                            <span className={`text-sm font-medium ${analysis.technicalSeo?.hasRobots ? 'text-green-400' : 'text-red-400'}`}>
                              {analysis.technicalSeo?.hasRobots ? 'Found' : 'Missing'}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-300">XML Sitemap</span>
                            <span className={`text-sm font-medium ${analysis.technicalSeo?.hasSitemap ? 'text-green-400' : 'text-red-400'}`}>
                              {analysis.technicalSeo?.hasSitemap ? 'Found' : 'Missing'}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-300">Page Title</span>
                            <span className={`text-sm font-medium ${analysis.technicalSeo?.hasTitle ? 'text-red-400' : 'text-green-400'}`}>
                              {analysis.technicalSeo?.hasTitle ? 'Missing' : 'Present'}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-300">Meta Description</span>
                            <span className={`text-sm font-medium ${analysis.technicalSeo?.hasMetaDesc ? 'text-red-400' : 'text-green-400'}`}>
                              {analysis.technicalSeo?.hasMetaDesc ? 'Missing' : 'Present'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    {analysis.lighthouseScores && (
                      <div className="mt-6 bg-gray-800 rounded-lg p-4">
                        <h4 className="text-md font-semibold mb-3">Lighthouse Scores</h4>
                        <div className="grid grid-cols-4 gap-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold" style={{color:"#2563EB"}}>{analysis.lighthouseScores.performance || 'N/A'}</div>
                            <div className="text-xs text-gray-300 mt-1">Performance</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold" style={{color:"#FCB900"}}>{analysis.lighthouseScores.seo || 'N/A'}</div>
                            <div className="text-xs text-gray-300 mt-1">SEO</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold" style={{color:"#A61F60"}}>{analysis.lighthouseScores.accessibility || 'N/A'}</div>
                            <div className="text-xs text-gray-300 mt-1">Accessibility</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold" style={{color:"#C2BACD"}}>{analysis.lighthouseScores.bestPractices || 'N/A'}</div>
                            <div className="text-xs text-gray-300 mt-1">Best Practices</div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="mt-6 bg-gray-800 rounded-lg p-4">
                      <h4 className="text-md font-semibold mb-3">Performance Metrics</h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-300">LCP</span>
                          <span className="text-sm text-white">{analysis.performance?.lcp || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-300">FID</span>
                          <span className="text-sm text-white">{analysis.performance?.fid || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-300">CLS</span>
                          <span className="text-sm text-white">{analysis.performance?.cls || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-300">TTFB</span>
                          <span className="text-sm text-white">{analysis.performance?.ttfb || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-300">Speed Index</span>
                          <span className="text-sm text-white">{analysis.performance?.speedIndex || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Content Tab */}
                {activeTab === 'content' && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Content Analysis</h3>
                    
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Content Metrics</h4>
                        <div className="space-y-3">
                          <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-gray-600">Average Word Count</span>
                            <span className="font-medium">1,245 words</span>
                          </div>
                          <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-gray-600">Reading Level</span>
                            <span className="font-medium">Grade 9</span>
                          </div>
                          <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-gray-600">Unique Content</span>
                            <span className="font-medium text-green-600">98%</span>
                          </div>
                          <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-gray-600">Internal Links</span>
                            <span className="font-medium">Avg 5 per page</span>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Content Opportunities</h4>
                        <div className="space-y-2">
                          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-sm font-medium text-blue-900">Add FAQ sections</p>
                            <p className="text-xs text-blue-700 mt-1">Improves chance of featured snippets</p>
                          </div>
                          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-sm font-medium text-blue-900">Increase content depth</p>
                            <p className="text-xs text-blue-700 mt-1">Target 2000+ words for competitive keywords</p>
                          </div>
                          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-sm font-medium text-blue-900">Add multimedia content</p>
                            <p className="text-xs text-blue-700 mt-1">Videos and infographics boost engagement</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Backlinks Tab */}
                {activeTab === 'backlinks' && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Backlink Analysis</h3>
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                        <p className="text-sm" style={{color:"#2563EB"}}>Total Backlinks</p>
                        <p className="text-2xl font-bold text-gray-900">{analysis.backlinks?.total === 'N/A' ? '0' : analysis.backlinks?.total || '0'}</p>
                      </div>
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                        <p className="text-sm" style={{color:"#A61F60"}}>Referring Domains</p>
                        <p className="text-2xl font-bold text-gray-900">{analysis.backlinks?.referringDomains || '0'}</p>
                      </div>
                      <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg p-4">
                        <p className="text-sm" style={{color:"#2563EB"}}>Domain Rating</p>
                        <p className="text-2xl font-bold text-gray-900">{analysis.backlinks?.domainRating || 'N/A'}</p>
                      </div>
                    </div>
                    {(!analysis.backlinks?.topReferring || analysis.backlinks?.topReferring?.length === 0) ? (
                      <div className="bg-gray-800 rounded-lg p-6 text-center">
                        <p className="text-gray-200 mb-2">No backlinks found for this domain.</p>
                        <p className="text-sm text-gray-300">This is common for new websites. Backlinks are built over time as other sites link to your content.</p>
                      </div>
                    ) : (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Top Referring Domains</h4>
                        <div className="space-y-2">
                          {analysis.backlinks.topReferring.map((ref, index) => (
                            <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                              <div className="flex items-center space-x-3">
                                <span className="text-lg font-semibold text-gray-400">#{index + 1}</span>
                                <div>
                                  <p className="font-medium text-gray-900">{ref.domain}</p>
                                  <p className="text-xs text-gray-500">DR: {ref.dr}</p>
                                </div>
                              </div>
                              <span className="text-sm text-gray-600">{ref.backlinks} links</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}


                {/* AI Visibility Tab */}
                {activeTab === 'ai' && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">AI Visibility Analysis</h3>
                    
                    <div className="mb-6">
                      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 border border-purple-200">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900">AI Overview Score</h4>
                            <p className="text-sm text-gray-600 mt-1">How well your content appears in AI-generated responses</p>
                          </div>
                          <div className="text-3xl font-bold text-purple-600">{analysis.aiScore || 82}/100</div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-white rounded-lg p-3">
                            <p className="text-sm text-gray-600">Featured in AI Responses</p>
                            <p className="font-semibold text-gray-900">23% of queries</p>
                          </div>
                          <div className="bg-white rounded-lg p-3">
                            <p className="text-sm text-gray-600">Citation Frequency</p>
                            <p className="font-semibold text-gray-900">High</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">AI Optimization Recommendations</h4>
                      <div className="space-y-3">
                        <div className="flex items-start p-3 bg-gray-50 rounded-lg">
                          <SparklesIcon className="h-5 w-5 text-purple-500 mr-3 mt-0.5" />
                          <div>
                            <p className="font-medium text-gray-900">Implement FAQ Schema</p>
                            <p className="text-sm text-gray-600">Structure your FAQs to be easily parsed by AI systems</p>
                          </div>
                        </div>
                        <div className="flex items-start p-3 bg-gray-50 rounded-lg">
                          <SparklesIcon className="h-5 w-5 text-purple-500 mr-3 mt-0.5" />
                          <div>
                            <p className="font-medium text-gray-900">Create Comprehensive Guides</p>
                            <p className="text-sm text-gray-600">Long-form, authoritative content is preferred by AI models</p>
                          </div>
                        </div>
                        <div className="flex items-start p-3 bg-gray-50 rounded-lg">
                          <SparklesIcon className="h-5 w-5 text-purple-500 mr-3 mt-0.5" />
                          <div>
                            <p className="font-medium text-gray-900">Use Clear Headers and Lists</p>
                            <p className="text-sm text-gray-600">Well-structured content is easier for AI to extract and reference</p>
                          </div>
                        </div>
                        <div className="flex items-start p-3 bg-gray-50 rounded-lg">
                          <SparklesIcon className="h-5 w-5 text-purple-500 mr-3 mt-0.5" />
                          <div>
                            <p className="font-medium text-gray-900">Build Topic Authority</p>
                            <p className="text-sm text-gray-600">Create topic clusters to establish expertise in your niche</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div></>
        )}

        {/* Empty State */}
        {!analysis && !loading && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <GlobeAltIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Start Your Domain Analysis</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Enter any domain to get comprehensive SEO insights, technical analysis, and AI visibility metrics.
            </p>
          </div>
        )}
      </div>
    
      {selectedIssue && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setSelectedIssue(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-bold text-gray-900">{selectedIssue.title || selectedIssue.message || 'Issue'}</h3>
              <button onClick={() => setSelectedIssue(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="mb-4">
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                selectedIssue.severity === 'critical' || selectedIssue.severity === 'high' ? 'bg-red-100 text-red-700' :
                selectedIssue.severity === 'warning' || selectedIssue.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                'bg-blue-100 text-blue-700'
              }`}>{selectedIssue.severity || 'info'}</span>
            </div>
            <p className="text-gray-600 mb-4">{selectedIssue.description || selectedIssue.fix || 'No details available.'}</p>
            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-blue-900 mb-2">How to Fix</h4>
              <p className="text-blue-800 text-sm">{
                (selectedIssue.title || '').includes('title') ? 'Add a descriptive <title> tag in your HTML <head>. Keep it under 60 characters and include your primary keyword.' :
                (selectedIssue.title || '').includes('meta description') ? 'Add a meta description tag in <head>. Keep it 150-160 characters with a compelling call to action.' :
                (selectedIssue.title || '').includes('SSL') ? 'Install an SSL certificate and redirect HTTP to HTTPS.' :
                (selectedIssue.title || '').includes('H1') ? 'Add a single <h1> tag with your main keyword. Each page should have exactly one H1 heading.' :
                (selectedIssue.title || '').includes('alt') ? 'Add descriptive alt attributes to all img tags for accessibility and SEO.' :
                (selectedIssue.title || '').includes('viewport') ? 'Add a viewport meta tag for mobile responsiveness.' :
                (selectedIssue.title || '').includes('robots') ? 'Create a robots.txt in your root directory with User-agent and Allow directives.' :
                (selectedIssue.title || '').includes('sitemap') ? 'Generate an XML sitemap and submit it to Google Search Console.' :
                (selectedIssue.title || '').includes('canonical') ? 'Add a canonical link tag in <head> to prevent duplicate content issues.' :
                (selectedIssue.title || '').includes('Thin') ? 'Add more content to your page. Aim for 300+ words of unique, valuable content.' :
                'Review this issue and follow SEO best practices to resolve it.'
              }</p>
            </div>
            <button onClick={() => setSelectedIssue(null)} className="w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 transition-colors">
              Got it
            </button>
          </div>
        </div>
      )}

      <FAQSection items={DOMAIN_FAQ} />
    </DashboardLayout>
    </>

      
  );
};

export default DomainAnalysis;